import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AdminSidebar from "../components/AdminSidebar";
import AgentSidebar from "../components/AgentSidebar";

const API = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

export default function InstitutionCourseTagsPage({ isAdmin = false }) {
  const { id } = useParams();
  const token = localStorage.getItem(isAdmin ? "adminToken" : "token");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    fieldOfStudy: "",
    degreeLevel: "",
  });
  const [saving, setSaving] = useState(false);
  const load = () =>
    fetch(`${API}/admin/institutions/${id}/courses`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((j) => {
        if (j.success) {
          setData(j.data);
          setSelected(
            j.data.courses.filter((c) => c.isTagged).map((c) => c.id),
          );
        }
      });
  useEffect(() => {
    load();
  }, [id]);

  const courses = useMemo(
    () =>
      (data?.courses || []).filter(
        (c) =>
          (!filters.search ||
            `${c.courseName} ${c.fieldOfStudy}`
              .toLowerCase()
              .includes(filters.search.toLowerCase())) &&
          (!filters.fieldOfStudy || c.fieldOfStudy === filters.fieldOfStudy) &&
          (!filters.degreeLevel || c.degreeLevel === filters.degreeLevel),
      ),
    [data, filters],
  );

  const fields = [...new Set((data?.courses || []).map((c) => c.fieldOfStudy))];
  const levels = [...new Set((data?.courses || []).map((c) => c.degreeLevel))];
  const toggle = (courseId) =>
    setSelected((s) =>
      s.includes(courseId) ? s.filter((x) => x !== courseId) : [...s, courseId],
    );
  const toggleFiltered = () => {
    const ids = courses.map((c) => c.id);
    setSelected((s) =>
      ids.every((id) => s.includes(id))
        ? s.filter((id) => !ids.includes(id))
        : [...new Set([...s, ...ids])],
    );
  };
  const save = async () => {
    setSaving(true);
    await fetch(`${API}/admin/institutions/${id}/courses`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ courseIds: selected, action: "replace" }),
    });
    setSaving(false);
    load();
  };
  const base = isAdmin ? "/admin/institutions" : "/agent/institutions";
  if (!data) return <p className="p-16 text-center">Loading courses…</p>;
  return (
    <div className="bg-[#f7f9fb] min-h-screen">
      {isAdmin ? <AdminSidebar /> : <AgentSidebar />}
      <main className="pt-16 md:pt-2 md:ml-64 p-6 md:p-8">
        <Link to={base} className="text-sm text-[#365da3] font-semibold">
          ← Back to institutions
        </Link>
        <h1 className="text-3xl font-bold text-[#00255a] mt-3">
          Tag courses: {data.institution.name}
        </h1>
        <p className="text-[#64748b] mt-2">
          Choose courses from {data.institution.country}. Selected courses will
          be shown as affiliated with this university.
        </p>
        <div className="mt-7 bg-white rounded-2xl border p-5">
          <div className="grid md:grid-cols-3 gap-3">
            <input
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              placeholder="Search courses or majors…"
              className="border rounded-lg px-4 py-3"
            />
            <select
              value={filters.fieldOfStudy}
              onChange={(e) =>
                setFilters({ ...filters, fieldOfStudy: e.target.value })
              }
              className="border rounded-lg px-4 py-3"
            >
              <option value="">All majors</option>
              {fields.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
            <select
              value={filters.degreeLevel}
              onChange={(e) =>
                setFilters({ ...filters, degreeLevel: e.target.value })
              }
              className="border rounded-lg px-4 py-3"
            >
              <option value="">All course types</option>
              {levels.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-between mt-5">
            <label className="font-semibold text-[#00255a]">
              <input
                type="checkbox"
                checked={
                  courses.length > 0 &&
                  courses.every((c) => selected.includes(c.id))
                }
                onChange={toggleFiltered}
                className="mr-2"
              />
              Select all filtered ({courses.length})
            </label>
            <button
              onClick={save}
              disabled={saving}
              className="bg-[#00255a] text-white px-5 py-3 rounded-lg font-bold disabled:opacity-60"
            >
              {saving ? "Saving…" : `Save ${selected.length} course tags`}
            </button>
          </div>
          <div className="mt-5 divide-y border rounded-xl">
            {courses.map((course) => (
              <label
                key={course.id}
                className="flex items-center gap-4 p-4 hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(course.id)}
                  onChange={() => toggle(course.id)}
                />
                <div className="flex-1">
                  <p className="font-bold text-[#00255a]">
                    {course.courseName}
                  </p>
                  <p className="text-sm text-[#64748b]">
                    {course.fieldOfStudy} · {course.degreeLevel}
                  </p>
                </div>
                {course.status !== "PUBLISHED" && (
                  <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-amber-100 text-amber-700">
                    {course.status.replace("_", " ")}
                  </span>
                )}
              </label>
            ))}
            {!courses.length && (
              <p className="p-8 text-center text-[#64748b]">
                No courses match these filters. {courses.length} courses are available in total.
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
